// app_client/src/pages/admin/AuditLogs.jsx
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Search, ShieldCheck } from 'lucide-react';
import DataTable from '../../components/common/DataTable';

const actionOptions = [
  { value: '', label: 'Tüm Eylemler' },
  { value: 'LOGIN', label: 'Giriş' },
  { value: 'LOGOUT', label: 'Çıkış' },
  { value: 'LOGIN_FAILED', label: 'Başarısız Giriş' },
  { value: 'CREATE', label: 'Oluşturma' },
  { value: 'UPDATE', label: 'Güncelleme' },
  { value: 'DELETE', label: 'Silme' },
  { value: 'CANCEL', label: 'İptal' },
  { value: 'STATUS_CHANGE', label: 'Statü Değişimi' }
];

const resourceOptions = [
  { value: '', label: 'Tüm Kaynaklar' },
  { value: 'auth', label: 'Auth' },
  { value: 'transaction', label: 'Finansal İşlem' },
  { value: 'invoice', label: 'Fatura' },
  { value: 'system', label: 'Sistem' },
  { value: 'operation', label: 'Operasyon' }
];

const formatDate = (value) => {
  if (!value) return '-';
  return new Intl.DateTimeFormat('tr-TR', {
    dateStyle: 'short',
    timeStyle: 'short'
  }).format(new Date(value));
};

const AuditLogs = () => {
  const [logs, setLogs] = useState([]);
  const [pagination, setPagination] = useState({ page: 1, limit: 20, total: 0, pages: 1 });
  const [filters, setFilters] = useState({ action: '', resourceType: '' });
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    const fetchLogs = async () => {
      setLoading(true);
      setError('');

      try {
        const { data } = await axios.get('/api/audit-logs', {
          params: {
            page,
            limit: pagination.limit,
            action: filters.action || undefined,
            resourceType: filters.resourceType || undefined
          }
        });

        setLogs(Array.isArray(data.data) ? data.data : []);
        setPagination(data.pagination || { page, limit: pagination.limit, total: 0, pages: 1 });
      } catch (err) {
        setError(err.response?.data?.message || 'Audit log kayıtları yüklenemedi.');
      } finally {
        setLoading(false);
      }
    };

    fetchLogs();
  }, [filters.action, filters.resourceType, page, pagination.limit]);

  const updateFilter = (field, value) => {
    setFilters((current) => ({ ...current, [field]: value }));
    setPage(1);
  };

  const lastPage = Math.max(pagination.pages || 1, 1);
  const columns = [
    {
      key: 'createdAt',
      header: 'Tarih',
      cellClassName: 'whitespace-nowrap text-xs font-bold text-slate-500',
      render: (log) => formatDate(log.createdAt)
    },
    {
      key: 'userName',
      header: 'Kullanıcı',
      cellClassName: 'text-sm font-black text-slate-800',
      render: (log) => log.userName || log.user?.email || 'Sistem'
    },
    {
      key: 'action',
      header: 'Eylem',
      render: (log) => (
        <span className="inline-flex rounded-xl bg-slate-100 px-3 py-1 text-[10px] font-black uppercase tracking-widest text-slate-600">
          {log.action}
        </span>
      )
    },
    {
      key: 'summary',
      header: 'Özet',
      cellClassName: 'text-sm font-semibold leading-6 text-slate-600',
      render: (log) => log.summary || log.description
    }
  ];

  return (
    <div className="mx-auto max-w-7xl space-y-6 p-6">
      <div className="flex flex-col gap-6 rounded-[2rem] border border-slate-100 bg-slate-50/60 p-5 lg:flex-row lg:items-center lg:justify-between">
        <div className="flex items-center gap-4">
          <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-blue-50 text-blue-600">
            <ShieldCheck size={24} />
          </div>
          <div>
            <h1 className="text-2xl font-black tracking-tight text-slate-900">Denetim Kayıtları</h1>
            <p className="text-sm font-medium text-slate-500">Tüm sistem aksiyonları tek tabloda</p>
          </div>
        </div>

        <div className="grid gap-3 sm:grid-cols-2 lg:w-[28rem]">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={15} />
            <select
              value={filters.resourceType}
              onChange={(event) => updateFilter('resourceType', event.target.value)}
              className="w-full appearance-none rounded-2xl border border-slate-200 bg-white py-3 pl-10 pr-4 text-xs font-black text-slate-600 outline-none focus:ring-4 focus:ring-blue-500/10"
            >
              {resourceOptions.map((option) => (
                <option key={option.value} value={option.value}>{option.label}</option>
              ))}
            </select>
          </div>

          <select
            value={filters.action}
            onChange={(event) => updateFilter('action', event.target.value)}
            className="w-full rounded-2xl border border-slate-200 bg-white px-4 py-3 text-xs font-black text-slate-600 outline-none focus:ring-4 focus:ring-blue-500/10"
          >
            {actionOptions.map((option) => (
              <option key={option.value} value={option.value}>{option.label}</option>
            ))}
          </select>
        </div>
      </div>

      <div className="overflow-hidden rounded-[2rem] border border-slate-100 bg-white shadow-sm">
        <DataTable
          columns={columns}
          data={logs}
          loading={loading}
          error={error}
          emptyText="Kayıt bulunamadı"
        />

        <div className="flex flex-col gap-3 border-t border-slate-50 bg-slate-50/30 p-5 sm:flex-row sm:items-center sm:justify-between">
          <p className="text-xs font-black uppercase tracking-widest text-slate-400">
            Toplam {pagination.total || 0} kayıt | Sayfa {pagination.page || page} / {lastPage}
          </p>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => setPage((current) => Math.max(current - 1, 1))}
              disabled={loading || page <= 1}
              className="rounded-xl border border-slate-200 bg-white px-4 py-2 text-xs font-black text-slate-600 transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-40"
            >
              Geri
            </button>
            <button
              type="button"
              onClick={() => setPage((current) => Math.min(current + 1, lastPage))}
              disabled={loading || page >= lastPage}
              className="rounded-xl border border-slate-200 bg-white px-4 py-2 text-xs font-black text-slate-600 transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-40"
            >
              İleri
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AuditLogs;
